import type { CargoProblemCategory, CargoProblemQueue, CargoProblemStatus } from '@magimp/shared';
import { useTranslation } from 'react-i18next';
import type { CargoProblemSummary } from '../../../api/cargo-problems.js';
import { useCargoProblemLabels } from '../labels.js';

export function CargoProblemsSummary({
  summary,
  selectedStatus,
  selectedCategory,
  selectedQueue,
  onStatus,
  onCategory,
  onQueue,
}: {
  summary: CargoProblemSummary;
  selectedStatus: string;
  selectedCategory: string;
  selectedQueue: string;
  onStatus: (value: CargoProblemStatus | '') => void;
  onCategory: (value: CargoProblemCategory | '') => void;
  onQueue: (value: CargoProblemQueue | '') => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  return (
    // WHY: These are server-provided, count-bearing dimensions; field-oriented FiltersBar
    // controls cannot preserve both the label and count without duplicating filter state.
    <div className="cargo-problems-summary" role="group" aria-label={t('summary.label')}>
      {summary.byStatus.length > 0 && (
        <div className="cargo-problems-summary-group" role="group" aria-label={t('summary.status')}>
          <span className="cargo-problems-summary-group-label">{t('summary.status')}</span>
          {summary.byStatus.map((item) => {
            const active = selectedStatus === item.value;
            return (
              <button
                key={item.value}
                type="button"
                aria-pressed={active}
                onClick={() => onStatus(active ? '' : item.value)}
              >
                <span>{labels.status(item.value)}</span>
                <strong>{item.count}</strong>
              </button>
            );
          })}
        </div>
      )}
      {summary.byCategory.length > 0 && (
        <div
          className="cargo-problems-summary-group"
          role="group"
          aria-label={t('summary.category')}
        >
          <span className="cargo-problems-summary-group-label">{t('summary.category')}</span>
          {summary.byCategory.map((item) => {
            const active = selectedCategory === item.value;
            return (
              <button
                key={item.value}
                type="button"
                aria-pressed={active}
                onClick={() => onCategory(active ? '' : item.value)}
              >
                <span>{labels.category(item.value)}</span>
                <strong>{item.count}</strong>
              </button>
            );
          })}
        </div>
      )}
      {summary.byQueue.length > 0 && (
        <div className="cargo-problems-summary-group" role="group" aria-label={t('summary.queue')}>
          <span className="cargo-problems-summary-group-label">{t('summary.queue')}</span>
          {summary.byQueue.map((item) => {
            const active = selectedQueue === item.value;
            return (
              <button
                key={item.value}
                type="button"
                aria-pressed={active}
                onClick={() => onQueue(active ? '' : item.value)}
              >
                <span>{labels.queue(item.value)}</span>
                <strong>{item.count}</strong>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
